import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useLocation } from "react-router-dom"
import SearchParams from "simplydo/core/searchParams"
import type { OpenAPI } from "simplydo/interfaces"

import api, { asQuery, websocketApi } from "api"
import PageLoadError from "components/lib/PageLoadError"
import { useAppSelector } from "store"
import util from "utils/utils"

import ProjectBoard from "./ProjectBoard"
import ProjectBoardSetup from "./ProjectBoardSetup"

export type ProjectSelectionModes = "multi"

type IProject = {
  forId: string
  ideas: OpenAPI.Schemas["Idea"][]
  ideasTotal: number
  ideasLoading: boolean
  laneImpactMeasures?: NonNullable<OpenAPI.GET<"/challenges/{id}/ideas">["response"]["laneImpactMeasures"]>

  canManageBoard?: boolean
  canViewBoard?: boolean

  defaultLane?: NonNullable<OpenAPI.GET<"/challenges/{id}">["response"]["projectBoard"]>["defaultLane"]

  previewId?: string
  setPreviewId: (ideaId: string) => void

  // Multi select
  selectionMode?: ProjectSelectionModes
  selectedIdeas: OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"]
  setSelectedIdeas: Dispatch<SetStateAction<OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"]>>

  laneFilter?: string[]

  checkUpdateProjectLane?: (ideaId: string, laneId: string, position: number) => void
  checkUpdateProjectLaneMulti?: (ideaIds: string[], laneId: string) => void
}

function Project({
  forId,
  ideas,
  ideasTotal,
  ideasLoading,
  laneImpactMeasures,
  canViewBoard,
  canManageBoard,
  defaultLane,
  laneFilter,

  setPreviewId,

  // Multi select
  selectionMode,
  selectedIdeas,
  setSelectedIdeas,

  checkUpdateProjectLane,
  checkUpdateProjectLaneMulti,
}: IProject) {
  const user = useAppSelector((state) => state.user)
  const searchParams = new SearchParams(window.location.search)
  const params = useLocation().search
  const queryIdea = useMemo(() => {
    const splitParams = (params || "").split("idea=")
    if (splitParams.length) {
      return splitParams[1]
    }
    return ""
  }, [params])

  const canManageChallenge = useMemo(() => util.canManageChallenge(user, { _id: forId }), [user, forId])
  const canViewProjectBoard = useMemo(
    () => util.hasPermission(user, "challenge.viewProjectBoard", forId),
    [user, forId],
  )

  const projectLaneQuery = useQuery({
    queryKey: ["boards", forId, "lanes"],
    queryFn: asQuery(api.boards.getProjectLanes, {
      params: [forId!],
    })!,
    enabled: canManageChallenge || canViewProjectBoard,
  })

  const [setupModalOpen, setSetupModalOpen] = useState<boolean>(false)

  const [highlightCard, setHighlightCard] = useState<string>(searchParams.has("idea") ? queryIdea : "")
  const projectWebsocket = useRef<ReturnType<(typeof websocketApi)["subscribe"]>>()
  const userId = user?._id

  const handleWebsocketData = useCallback(
    (data: {
      lanes: OpenAPI.Schemas["ProjectLane"][]
      ideas: OpenAPI.Schemas["Idea"][]
      channel: string
      fromUser: string
    }) => {
      const { channel, fromUser } = data

      if (fromUser === userId) {
        return
      }

      switch (channel) {
        case "lanesUpdated":
          api.queryClient.invalidateQueries({ queryKey: ["boards", forId, "lanes"] })
          break
        case "ideasUpdated":
          api.queryClient.invalidateQueries({ queryKey: ["challenges", forId, "ideas"] })
          break
        default:
          break
      }
    },
    [userId, forId],
  )

  const handleWebsocketLanes = useCallback(() => {
    if (projectWebsocket?.current) {
      projectWebsocket.current.publish("lanesUpdated")
    }
  }, [])

  const handleWebsocketIdeas = useCallback(() => {
    if (projectWebsocket?.current) {
      projectWebsocket.current.publish("ideasUpdated")
    }
  }, [])

  useEffect(() => {
    if (!forId) return
    // @ts-ignore
    projectWebsocket.current = websocketApi.subscribe(`projectBoard-${forId}`, (data) => handleWebsocketData(data))
    return () => {
      if (projectWebsocket.current) {
        projectWebsocket.current.unsubscribe()
      }
    }
  }, [handleWebsocketData, forId])

  if (!user) return <PageLoadError title={"Please log in to view the project board"} message={undefined} />
  if (!canViewBoard && !util.hasPermission(user, "challenge.viewProjectBoard", forId)) return null

  return (
    <>
      <ProjectBoardSetup
        forId={forId}
        lanes={projectLaneQuery.data?.lanes || []}
        dataLoaded={projectLaneQuery.isSuccess}
        setLanes={(newLanes) => {
          api.queryClient.setQueryData(["boards", forId, "lanes"], (oldData: any) => {
            return {
              ...(oldData || {}),
              lanes: newLanes,
            }
          })
        }}
        setupModalOpen={setupModalOpen}
        setSetupModalOpen={setSetupModalOpen}
        laneFilter={laneFilter}
        canManageBoard={canManageBoard}
      />
      <ProjectBoard
        forId={forId}
        ideas={ideas}
        ideasTotal={ideasTotal}
        ideasLoading={ideasLoading}
        laneImpactMeasures={laneImpactMeasures || {}}
        defaultLane={defaultLane}
        laneFilter={laneFilter}
        setPreviewId={setPreviewId}
        highlightCard={highlightCard}
        handleWebsocketIdeas={handleWebsocketIdeas}
        handleWebsocketLanes={handleWebsocketLanes}
        canManageBoard={canManageBoard}
        checkUpdateProjectLane={checkUpdateProjectLane}
        checkUpdateProjectLaneMulti={checkUpdateProjectLaneMulti}
        setSetupModalOpen={setSetupModalOpen}
        selectionMode={selectionMode}
        selectedIdeas={selectedIdeas}
        setSelectedIdeas={setSelectedIdeas}
      />
    </>
  )
}

export default Project
